import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { useEffect, useRef } from "react";
import * as THREE from "three";
import "../App.css";
import { useTheme } from "../context/ThemeContext";
import InstallPrompt from "../utility/InstallPrompt";
import InstallButton from "../utility//InstallButton";

export default function LandingPage() {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const mountRef = useRef(null);
  const prefersReducedMotion = useReducedMotion(); // Check if user prefers reduced motion

  const handleProceed = () => {
    navigate(currentUser ? "/home" : "/auth");
  };

  // Animation variants for smooth transitions
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.4, // Disable animation if reduced motion is enabled
        ease: "easeOut",
        when: "beforeChildren",
        staggerChildren: prefersReducedMotion ? 0 : 0.1, // Disable stagger if reduced motion is enabled
      },
    },
  };

  const itemVariants = {
    hidden: { y: prefersReducedMotion ? 0 : 30, opacity: 0 }, // Disable y-offset if reduced motion is enabled
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 150,
        damping: 10,
        duration: prefersReducedMotion ? 0 : 0.4, // Disable animation if reduced motion is enabled
      },
    },
  };

  const buttonVariants = {
    hover: {
      scale: prefersReducedMotion ? 1 : 1.05, // Disable hover effect if reduced motion is enabled
      transition: { type: "spring", stiffness: 300 },
    },
    tap: { scale: prefersReducedMotion ? 1 : 0.95 }, // Disable tap effect if reduced motion is enabled
  };

  useEffect(() => {
    if (!mountRef.current || prefersReducedMotion) return; // Skip Three.js if reduced motion is enabled

    // Scene, Camera, Renderer
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(
      75,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    mountRef.current.appendChild(renderer.domElement);

    // Create Paper Sheets
    const paperGeometry = new THREE.PlaneGeometry(2, 2.8);
    const paperMaterial = new THREE.MeshBasicMaterial({
      color: darkMode ? 0xffffff : 0x333333,
      transparent: true,
      opacity: darkMode ? 0.3 : 0.5,
      side: THREE.DoubleSide,
    });

    const papers = [];
    const numPapers = 200; // Reduced number of papers for better performance on mobile

    const randomizePaperPosition = (paper) => {
      paper.position.set(
        ((Math.random() - 0.5) * window.innerWidth) / 10,
        ((Math.random() - 0.5) * window.innerHeight) / 10,
        Math.random() * -400
      );
      paper.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
      paper.scale.set(0.3 + Math.random() * 0.2, 0.3 + Math.random() * 0.2, 1);
    };

    for (let i = 0; i < numPapers; i++) {
      const paper = new THREE.Mesh(paperGeometry, paperMaterial);
      randomizePaperPosition(paper);
      scene.add(paper);
      papers.push(paper);
    }

    const ambientLight = new THREE.AmbientLight(0x404040, 2);
    scene.add(ambientLight);

    camera.position.z = 20;

    const animate = () => {
      requestAnimationFrame(animate);

      papers.forEach((paper) => {
        paper.rotation.x += 0.001;
        paper.rotation.y += 0.001;
        paper.position.y += 0.05;

        if (paper.position.y > 20) {
          randomizePaperPosition(paper);
          paper.position.y = -20;
        }
      });

      renderer.render(scene, camera);
    };

    animate();

    const handleResize = () => {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (mountRef.current) {
        mountRef.current.removeChild(renderer.domElement);
      }
    };
  }, [darkMode, prefersReducedMotion]);

  return (
    <motion.div
      className="relative min-h-screen w-full flex flex-col items-center justify-between text-gray-800 dark:text-white bg-gradient-to-b from-gray-100 to-gray-300 dark:from-gray-900 dark:to-gray-800 transition-all overflow-hidden"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      style={{ willChange: "opacity" }}
    >
      {/* Three.js Canvas */}
      <div ref={mountRef} className="absolute inset-0 z-0"></div>

      {/* Main Content */}
      <motion.div
        className="relative z-10 flex flex-col justify-center items-center text-center px-5 sm:px-8 md:px-10 max-w-lg flex-grow"
        variants={containerVariants}
      >
        <motion.h1
          className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-6 text-gray-900 dark:text-white drop-shadow-lg"
          variants={itemVariants}
          style={{ willChange: "transform, opacity" }}
        >
          PrintKaro<span className="text-blue-600 dark:text-blue-400">.shop</span>
        </motion.h1>
        <motion.p
          className="text-lg sm:text-xl font-medium text-gray-700 dark:text-gray-300 drop-shadow-md leading-relaxed"
          variants={itemVariants}
          style={{ willChange: "transform, opacity" }}
        >
          No more long queues! Upload, print, and pick up hassle-free.
        </motion.p>

        <motion.button
          onClick={handleProceed}
          className="mt-8 px-6 py-3 bg-blue-600 text-white font-semibold rounded-full shadow-lg hover:bg-blue-500 dark:bg-blue-500 dark:hover:bg-blue-400 transition duration-300 transform focus:outline-none focus:ring-4 focus:ring-blue-300 focus:ring-offset-2"
          aria-label="Get Started"
          variants={buttonVariants}
          whileHover="hover"
          whileTap="tap"
          style={{ willChange: "transform" }}
        >
          Get Started →
        </motion.button>

          <motion.div 
            variants={itemVariants}
            className="w-full mt-3 flex justify-center"
          >
            <InstallButton />
          </motion.div>
        

        {/* Why Choose Us Section */}
        <motion.div className="w-full mt-3 space-y-4 overflow-hidden" variants={containerVariants}>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[
              { title: "🚀 Fast & Easy", desc: "Upload & print in minutes." },
              { title: "💰 Affordable", desc: "Best prices for high-quality prints." },
              { title: "📍 Convenient", desc: "Pick up anytime, no waiting." },
              { title: "🔒 Secure", desc: "Data privacy guaranteed." },
            ].map((item, index) => (
              <motion.div
                key={index}
                className="p-4 bg-white dark:bg-gray-800 shadow-lg rounded-xl text-center transition-all hover:scale-105 border-2 border-transparent bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 dark:from-gray-800 dark:via-gray-700 dark:to-gray-800 relative"
                variants={itemVariants}
                whileHover={{
                  scale: prefersReducedMotion ? 1 : 1.05,
                  transition: { type: "spring", stiffness: 200 },
                }}
                style={{ willChange: "transform, opacity" }}
              >
                <div className="absolute inset-0 m-[1px] rounded-xl bg-white dark:bg-gray-900"></div>
                <div className="relative z-10">
                  <h3 className="text-lg font-semibold">{item.title}</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </motion.div>

      {/* Footer */}
      <motion.footer
        className="relative z-10 w-full text-center p-4 bg-opacity-80 backdrop-blur-md text-gray-700 dark:text-gray-300 text-sm"
        variants={itemVariants}
        style={{ willChange: "transform, opacity" }}
      >
        <div className="flex flex-wrap justify-center gap-3 text-xs sm:text-sm font-medium">
          <Link to="/privacy-policy" className="footer-link">
            Privacy Policy
          </Link>
          <Link to="/terms" className="footer-link">
            Terms & Conditions
          </Link>
          <Link to="/refund-policy" className="footer-link">
            Cancellation and Refunds
          </Link>
          <Link to="/contact-us" className="footer-link">
            Contact Us
          </Link>
          <Link to="/shippingPolicy" className="footer-link">
            Shipping Policy
          </Link>
          <Link to="/developer_page" className="footer-link">
            Developer
          </Link>
          <Link to="/pricing" className="footer-link">
            Pricing
          </Link>
        </div>
        <p className="text-xs mt-2">
          © {new Date().getFullYear()} PrintKaro.shop. All rights reserved.
        </p>
      </motion.footer>
      <InstallPrompt />
    </motion.div>
  );
}
